import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title?: string;
  description?: string;
  canonical?: string;
  ogImage?: string;
  ogType?: 'website' | 'article';
  twitterCard?: 'summary' | 'summary_large_image';
  noIndex?: boolean;
  schema?: Record<string, any>;
}

export const SEO: React.FC<SEOProps> = ({
  title = 'Cássio Lemos - Portfólio 2026 | Desenvolvedor Fullstack & Designer de Interface',
  description = 'Cássio Lemos - Portfólio 2026: Desenvolvedor Fullstack e Designer de Interface especializado em criar experiências digitais impactantes. Transforme sua visão em realidade com design premium e desenvolvimento de alta performance.',
  canonical = 'https://cassio.art/',
  ogImage = 'https://cassio.art/og-image.jpg',
  ogType = 'website',
  twitterCard = 'summary_large_image',
  noIndex = false,
  schema,
}) => {
  const fullTitle = title.includes('Cássio Lemos') ? title : `${title} | Cássio Lemos - Portfólio 2026`;

  return (
    <Helmet>
      {/* Meta Tags Básicas */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />

      {/* Robots */}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="Cássio Lemos - Portfólio 2026" />
      <meta property="og:locale" content="pt_BR" />

      {/* Twitter Card */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:url" content={canonical} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* Additional SEO */}
      <meta name="theme-color" content="#3B82F6" />
      <meta name="msapplication-TileColor" content="#3B82F6" />
      <meta name="msapplication-config" content="/browserconfig.xml" />

      {/* Schema Markup JSON-LD */}
      {schema && (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;

export const homepageSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Cássio Lemos - Portfólio 2026",
  "url": "https://cassio.art",
  "logo": "https://cassio.art/logo.png",
  "description": "Desenvolvedor Fullstack e Designer de Interface especializado em criar experiências digitais impactantes",
  "address": {
    "@type": "PostalAddress",
    "addressLocality": "Rio de Janeiro",
    "addressRegion": "RJ",
    "addressCountry": "BR"
  },
  "contactPoint": {
    "@type": "ContactPoint",
    "telephone": "+55-21-99999-9999",
    "contactType": "customer service"
  },
  "sameAs": [
    "https://linkedin.com/in/cassiodesign",
    "https://instagram.com/cassiodesign",
    "https://behance.net/cassiodesign",
    "https://github.com/cassiodesign"
  ]
};

export const personSchema = {
  "@context": "https://schema.org",
  "@type": "Person",
  "name": "Cássio",
  "jobTitle": "Diretor de Arte & Designer Gráfico",
  "description": "Especialista em design de interface, desenvolvimento fullstack e criação de personagens digitais com IA",
  "image": "https://cassio.art/profile.jpg",
  "url": "https://cassio.art/sobre",
  "sameAs": [
    "https://linkedin.com/in/cassiodesign",
    "https://instagram.com/cassiodesign",
    "https://behance.net/cassiodesign"
  ],
  "worksFor": {
    "@type": "Organization",
    "name": "Cássio Lemos - Portfólio 2026"
  },
  "knowsAbout": [
    "UI Design",
    "UX Design",
    "React",
    "Next.js",
    "Node.js",
    "TypeScript",
    "Design Gráfico",
    "Branding",
    "Identidade Visual",
    "Desenvolvimento Web",
    "Fullstack Development"
  ]
};

export const creativeWorkSchema = (title: string, description: string, image: string, dateCreated: string) => ({
  "@context": "https://schema.org",
  "@type": "CreativeWork",
  "name": title,
  "description": description,
  "image": image,
  "author": {
    "@type": "Person",
    "name": "Cássio"
  },
  "dateCreated": dateCreated,
  "keywords": "design gráfico, desenvolvimento web, UI design, branding",
  "inLanguage": "pt-BR"
});

export const breadcrumbListSchema = (items: Array<{ name: string; url: string }>) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": item.url
  }))
});
